import React, { useState } from 'react';
import {
  Box, VStack, HStack, Text, Heading, Badge, Button,
  Container, Flex, Icon, SimpleGrid, Table, Thead, Tbody, Tr, Th, Td, Checkbox, Select,
  Modal, ModalOverlay, ModalContent, ModalBody, useDisclosure, useToast,
  Divider, Grid, GridItem,
} from '@chakra-ui/react';
import {
  FaCreditCard, FaCheckCircle, FaLock, FaPlus, FaTimes,
  FaWallet, FaRegCreditCard,
  FaVial, FaStethoscope, FaSyringe, FaEye, FaPrint,
} from 'react-icons/fa';

const formatCurrency = (amount) => `$${parseFloat(amount || 0).toFixed(2)}`;
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Mock data
const MOCK_LAB = { id: 'demo', name: 'Sunrise Diagnostics', email: 'billing@sunrisedx.com' };

const MOCK_INVOICES = [
  { id: '1', invoice_number: 'INV-2024-0047', payment_status: 'unpaid', created_at: '2024-03-15T10:00:00Z', batch_number: 'BATCH-0047', draw_count: 5, total_amount: 125.00 },
  { id: '2', invoice_number: 'INV-2024-0046', payment_status: 'unpaid', created_at: '2024-03-12T09:00:00Z', batch_number: 'BATCH-0046', draw_count: 3, total_amount: 75.00 },
  { id: '3', invoice_number: 'INV-2024-0045', payment_status: 'unpaid', created_at: '2024-03-08T14:00:00Z', batch_number: 'BATCH-0045', draw_count: 8, total_amount: 200.00 },
  { id: '4', invoice_number: 'INV-2024-0044', payment_status: 'paid', created_at: '2024-03-01T11:00:00Z', batch_number: 'BATCH-0044', draw_count: 4, total_amount: 100.00 },
  { id: '5', invoice_number: 'INV-2024-0043', payment_status: 'paid', created_at: '2024-02-25T08:00:00Z', batch_number: 'BATCH-0043', draw_count: 6, total_amount: 150.00 },
  { id: '6', invoice_number: 'INV-2024-0042', payment_status: 'paid', created_at: '2024-02-18T16:00:00Z', batch_number: 'BATCH-0042', draw_count: 2, total_amount: 50.00 },
];

// Detailed invoice data for the overlay
const MOCK_INVOICE_DETAILS = {
  '1': {
    line_items: [
      { patient_name: 'John Smith', patient_dob: '1985-06-12', accession: 'ACC-90821', doctor_name: 'Dr. Sarah Chen', phlebotomist_name: 'Maria Garcia', collection_date: '2024-03-14', tests: ['CBC', 'CMP', 'Lipid Panel'], diagnosis: 'Z00.00 - General adult examination', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 5.00 },
      { patient_name: 'Emily Davis', patient_dob: '1992-11-03', accession: 'ACC-90822', doctor_name: 'Dr. Sarah Chen', phlebotomist_name: 'Maria Garcia', collection_date: '2024-03-14', tests: ['TSH', 'Free T4'], diagnosis: 'E03.9 - Hypothyroidism', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Robert Johnson', patient_dob: '1978-02-28', accession: 'ACC-90823', doctor_name: 'Dr. James Lee', phlebotomist_name: 'Maria Garcia', collection_date: '2024-03-14', tests: ['HbA1c', 'Glucose'], diagnosis: 'E11.9 - Type 2 diabetes', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Lisa Wong', patient_dob: '1990-08-17', accession: 'ACC-90824', doctor_name: 'Dr. James Lee', phlebotomist_name: 'Maria Garcia', collection_date: '2024-03-15', tests: ['Vitamin D', 'B12', 'Iron Panel'], diagnosis: 'D50.9 - Iron deficiency anemia', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Michael Brown', patient_dob: '1965-04-22', accession: 'ACC-90825', doctor_name: 'Dr. Sarah Chen', phlebotomist_name: 'Maria Garcia', collection_date: '2024-03-15', tests: ['PSA', 'CBC'], diagnosis: 'Z12.5 - Prostate screening', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
    ],
  },
  '2': {
    line_items: [
      { patient_name: 'Sandra Miller', patient_dob: '1988-01-15', accession: 'ACC-90810', doctor_name: 'Dr. Amy Park', phlebotomist_name: 'James Wilson', collection_date: '2024-03-11', tests: ['CBC', 'BMP'], diagnosis: 'R53.83 - Fatigue', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 5.00 },
      { patient_name: 'David Chen', patient_dob: '1975-09-30', accession: 'ACC-90811', doctor_name: 'Dr. Amy Park', phlebotomist_name: 'James Wilson', collection_date: '2024-03-11', tests: ['Liver Panel', 'GGT'], diagnosis: 'K76.0 - Fatty liver', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Karen Taylor', patient_dob: '1970-12-05', accession: 'ACC-90812', doctor_name: 'Dr. Amy Park', phlebotomist_name: 'James Wilson', collection_date: '2024-03-12', tests: ['Thyroid Panel'], diagnosis: 'E05.90 - Thyrotoxicosis', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
    ],
  },
  '3': {
    line_items: [
      { patient_name: 'Patricia Anderson', patient_dob: '1982-07-19', accession: 'ACC-90790', doctor_name: 'Dr. Sarah Chen', phlebotomist_name: 'Maria Garcia', collection_date: '2024-03-07', tests: ['CBC', 'CMP', 'UA'], diagnosis: 'Z00.00 - General examination', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 5.00 },
      { patient_name: 'Thomas Wright', patient_dob: '1955-03-11', accession: 'ACC-90791', doctor_name: 'Dr. James Lee', phlebotomist_name: 'Maria Garcia', collection_date: '2024-03-07', tests: ['PT/INR', 'CBC'], diagnosis: 'I48.91 - Atrial fibrillation', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Jennifer Lopez', patient_dob: '1995-05-25', accession: 'ACC-90792', doctor_name: 'Dr. Sarah Chen', phlebotomist_name: 'Maria Garcia', collection_date: '2024-03-07', tests: ['hCG', 'CBC', 'Blood Type'], diagnosis: 'Z34.00 - Pregnancy supervision', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'William Harris', patient_dob: '1960-10-08', accession: 'ACC-90793', doctor_name: 'Dr. James Lee', phlebotomist_name: 'James Wilson', collection_date: '2024-03-08', tests: ['Renal Panel', 'CBC'], diagnosis: 'N18.3 - CKD stage 3', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Nancy Clark', patient_dob: '1987-01-30', accession: 'ACC-90794', doctor_name: 'Dr. Amy Park', phlebotomist_name: 'James Wilson', collection_date: '2024-03-08', tests: ['Celiac Panel', 'IgA'], diagnosis: 'K90.0 - Celiac disease', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Richard Martin', patient_dob: '1972-08-14', accession: 'ACC-90795', doctor_name: 'Dr. Amy Park', phlebotomist_name: 'James Wilson', collection_date: '2024-03-08', tests: ['Testosterone', 'SHBG', 'LH'], diagnosis: 'E29.1 - Testicular hypofunction', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Susan Robinson', patient_dob: '1968-04-02', accession: 'ACC-90796', doctor_name: 'Dr. Sarah Chen', phlebotomist_name: 'Maria Garcia', collection_date: '2024-03-08', tests: ['ANA', 'ESR', 'CRP'], diagnosis: 'M35.9 - Autoimmune disease', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Daniel Jackson', patient_dob: '1980-12-20', accession: 'ACC-90797', doctor_name: 'Dr. James Lee', phlebotomist_name: 'Maria Garcia', collection_date: '2024-03-08', tests: ['Cortisol AM', 'ACTH'], diagnosis: 'E27.1 - Adrenal insufficiency', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
    ],
  },
  '4': {
    line_items: [
      { patient_name: 'Angela White', patient_dob: '1991-09-14', accession: 'ACC-90770', doctor_name: 'Dr. Sarah Chen', phlebotomist_name: 'Maria Garcia', collection_date: '2024-02-29', tests: ['CBC', 'Ferritin'], diagnosis: 'D50.9 - Iron deficiency anemia', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 5.00 },
      { patient_name: 'George Thompson', patient_dob: '1958-06-07', accession: 'ACC-90771', doctor_name: 'Dr. James Lee', phlebotomist_name: 'Maria Garcia', collection_date: '2024-02-29', tests: ['BNP', 'CMP'], diagnosis: 'I50.9 - Heart failure', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Betty Garcia', patient_dob: '1983-03-22', accession: 'ACC-90772', doctor_name: 'Dr. Amy Park', phlebotomist_name: 'James Wilson', collection_date: '2024-03-01', tests: ['Prolactin', 'TSH'], diagnosis: 'E22.1 - Hyperprolactinemia', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Edward Martinez', patient_dob: '1976-11-18', accession: 'ACC-90773', doctor_name: 'Dr. Amy Park', phlebotomist_name: 'James Wilson', collection_date: '2024-03-01', tests: ['Uric Acid', 'CMP'], diagnosis: 'M10.9 - Gout', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
    ],
  },
  '5': {
    line_items: [
      { patient_name: 'Dorothy Lee', patient_dob: '1969-02-14', accession: 'ACC-90750', doctor_name: 'Dr. Sarah Chen', phlebotomist_name: 'Maria Garcia', collection_date: '2024-02-24', tests: ['Hemoglobin', 'Reticulocyte Count'], diagnosis: 'D64.9 - Anemia', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 5.00 },
      { patient_name: 'Frank Wilson', patient_dob: '1952-07-30', accession: 'ACC-90751', doctor_name: 'Dr. James Lee', phlebotomist_name: 'Maria Garcia', collection_date: '2024-02-24', tests: ['Troponin', 'CK-MB', 'BNP'], diagnosis: 'I25.10 - CAD', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Helen Brown', patient_dob: '1994-10-05', accession: 'ACC-90752', doctor_name: 'Dr. Amy Park', phlebotomist_name: 'James Wilson', collection_date: '2024-02-24', tests: ['Estradiol', 'FSH', 'LH'], diagnosis: 'N91.2 - Amenorrhea', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Jack Davis', patient_dob: '1981-05-19', accession: 'ACC-90753', doctor_name: 'Dr. Sarah Chen', phlebotomist_name: 'Maria Garcia', collection_date: '2024-02-25', tests: ['Hepatitis Panel', 'Liver Function'], diagnosis: 'B18.2 - Chronic Hep C', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Irene Nguyen', patient_dob: '1986-08-11', accession: 'ACC-90754', doctor_name: 'Dr. James Lee', phlebotomist_name: 'James Wilson', collection_date: '2024-02-25', tests: ['Vitamin D', 'Calcium', 'PTH'], diagnosis: 'E55.9 - Vitamin D deficiency', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
      { patient_name: 'Kevin Moore', patient_dob: '1973-12-28', accession: 'ACC-90755', doctor_name: 'Dr. Amy Park', phlebotomist_name: 'James Wilson', collection_date: '2024-02-25', tests: ['PSA', 'Testosterone'], diagnosis: 'Z12.5 - Prostate screening', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
    ],
  },
  '6': {
    line_items: [
      { patient_name: 'Laura Adams', patient_dob: '1997-04-09', accession: 'ACC-90730', doctor_name: 'Dr. Sarah Chen', phlebotomist_name: 'Maria Garcia', collection_date: '2024-02-17', tests: ['CBC', 'CMP'], diagnosis: 'Z00.00 - General examination', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 5.00 },
      { patient_name: 'Mark Evans', patient_dob: '1963-11-23', accession: 'ACC-90731', doctor_name: 'Dr. James Lee', phlebotomist_name: 'Maria Garcia', collection_date: '2024-02-18', tests: ['Lipid Panel', 'HbA1c'], diagnosis: 'E78.5 - Hyperlipidemia', draw_fee: 15.00, admin_fee: 5.00, ship_fee: 0 },
    ],
  },
};

const MOCK_CARDS = [
  { id: 'pm_1', brand: 'visa', last4: '4242', exp_month: 12, exp_year: 2026 },
  { id: 'pm_2', brand: 'mastercard', last4: '8888', exp_month: 6, exp_year: 2025 },
];

export default function LabInvoicesPreview() {
  const [invoices, setInvoices] = useState(MOCK_INVOICES);
  const [savedCards, setSavedCards] = useState(MOCK_CARDS);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [selectedCard, setSelectedCard] = useState(MOCK_CARDS[0]?.id || '');
  const [viewingInvoice, setViewingInvoice] = useState(null); // invoice being viewed in overlay
  const [paymentTab, setPaymentTab] = useState('saved');
  const [paymentSuccess, setPaymentSuccess] = useState({});
  const [pendingChargeCardId, setPendingChargeCardId] = useState(null);
  const redactPHI = true;
  const [printHtml, setPrintHtml] = useState(null);
  const { isOpen: isAddCardOpen, onOpen: onAddCardOpen, onClose: onAddCardClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const { isOpen: isInvoicePayOpen, onOpen: onInvoicePayOpen, onClose: onInvoicePayClose } = useDisclosure();
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const { isOpen: isPrintOpen, onOpen: onPrintOpen, onClose: onPrintClose } = useDisclosure();
  const toast = useToast();

  const unpaidInvoices = invoices.filter(inv => inv.payment_status !== 'paid');
  const paidInvoices = invoices.filter(inv => inv.payment_status === 'paid');
  const selectedTotal = selectedInvoices.reduce((sum, id) => {
    const inv = invoices.find(i => i.id === id);
    return sum + (inv?.total_amount || 0);
  }, 0);

  const toggleInvoice = (id) => {
    setSelectedInvoices(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAllUnpaid = () => {
    if (selectedInvoices.length === unpaidInvoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(unpaidInvoices.map(i => i.id));
    }
  };

  const openInvoiceDetail = (inv) => {
    setViewingInvoice(inv);
    setPaymentTab('saved');
    onViewOpen();
  };

  // Show confirmation before charging saved card
  const handleChargeSavedCard = (cardId) => {
    if (selectedInvoices.length === 0) {
      toast({ title: 'Select invoices', description: 'Please select invoices to pay.', status: 'warning', duration: 3000 });
      return;
    }
    setPendingChargeCardId(cardId);
    onConfirmOpen();
  };

  // Actually process the charge after confirmation
  const confirmCharge = () => {
    const card = savedCards.find(c => c.id === pendingChargeCardId);
    const chargedTotal = selectedTotal;
    setInvoices(prev => prev.map(inv =>
      selectedInvoices.includes(inv.id) ? { ...inv, payment_status: 'paid' } : inv
    ));
    setSelectedInvoices([]);
    onConfirmClose();
    setPendingChargeCardId(null);
    toast({
      title: 'Payment successful!',
      description: `Charged ${formatCurrency(chargedTotal)} to ${card.brand} •••• ${card.last4}`,
      status: 'success', duration: 4000,
    });
  };

  // Mock: pay single invoice from detail overlay
  const handleInvoicePay = () => {
    if (viewingInvoice) {
      setInvoices(prev => prev.map(inv =>
        inv.id === viewingInvoice.id ? { ...inv, payment_status: 'paid' } : inv
      ));
      setPaymentSuccess(prev => ({ ...prev, [viewingInvoice.id]: true }));
      setViewingInvoice(prev => prev ? { ...prev, payment_status: 'paid' } : prev);
    }
    onInvoicePayClose();
    toast({ title: 'Payment successful!', description: `Paid ${formatCurrency(viewingInvoice?.total_amount)}`, status: 'success', duration: 3000 });
  };

  // Mock: simulate adding a card
  const handleMockAddCard = () => {
    const newCard = { id: `pm_${Date.now()}`, brand: 'amex', last4: '1234', exp_month: 3, exp_year: 2027 };
    setSavedCards(prev => [...prev, newCard]);
    setSelectedCard(newCard.id);
    onAddCardClose();
    toast({ title: 'Card saved!', description: 'Your card has been saved on file.', status: 'success', duration: 3000 });
  };

  // Get detail data for the viewing invoice
  const viewingDetail = viewingInvoice ? MOCK_INVOICE_DETAILS[viewingInvoice.id] : null;
  const isViewingPaid = viewingInvoice && (viewingInvoice.payment_status === 'paid' || paymentSuccess[viewingInvoice?.id]);

  // Redact helper — replaces text with blurred placeholder
  const redact = (text) => redactPHI ? '••••••••' : text;

  // Partial display helpers — show only last name & birth year
  const getLastName = (fullName) => {
    if (!fullName) return '—';
    const parts = fullName.trim().split(' ');
    return parts[parts.length - 1];
  };
  const getBirthYear = (dobStr) => {
    if (!dobStr) return '—';
    return new Date(dobStr).getFullYear().toString();
  };

  // Build invoice HTML for printing
  const buildInvoiceHtml = (inv) => {
    const detail = MOCK_INVOICE_DETAILS[inv.id];
    if (!detail) return '';
    const isPaid = inv.payment_status === 'paid' || paymentSuccess[inv.id];
    const totalDraw = detail.line_items.reduce((s, i) => s + (i.draw_fee || 0), 0);
    const totalAdmin = detail.line_items.reduce((s, i) => s + (i.admin_fee || 0), 0);
    const totalShip = detail.line_items.reduce((s, i) => s + (i.ship_fee || 0), 0);
    return `
    <div class="invoice-page">
      <div class="header">
        <div class="header-flex">
          <div>
            <div class="brand">AtHomeBloodDraw.com</div>
            <div class="title">INVOICE</div>
            <div class="subtitle">Mobile Phlebotomy Services</div>
          </div>
          <div style="text-align:right">
            <div class="inv-label">Invoice No.</div>
            <div class="inv-number">${inv.invoice_number}</div>
            <div class="inv-date">Issued ${formatDate(inv.created_at)}</div>
          </div>
        </div>
      </div>
      <div class="status-bar ${isPaid ? 'paid' : 'unpaid'}">
        <span class="status-text ${isPaid ? 'paid' : 'unpaid'}">${isPaid ? '&#10004; Payment Received' : '&#9888; Payment Required'}</span>
        <span class="status-amount ${isPaid ? 'paid' : 'unpaid'}">${formatCurrency(inv.total_amount)}</span>
      </div>
      <div class="bill-to">
        <div>
          <div class="bill-label">Bill To</div>
          <div class="bill-name">${MOCK_LAB.name}</div>
          <div class="bill-email">${MOCK_LAB.email}</div>
        </div>
        <div style="text-align:right">
          <div class="bill-label">Status</div>
          <span class="badge ${isPaid ? 'paid' : 'unpaid'}">${isPaid ? 'PAID' : 'UNPAID'}</span>
        </div>
      </div>
      <hr class="divider">
      <div class="items">
        <div class="items-header">
          <span class="col-detail">Service Details</span>
          <span class="col-date">Collection Date</span>
          <span class="col-amount">Amount</span>
        </div>
        ${detail.line_items.map(item => {
          const itemTotal = (item.draw_fee || 0) + (item.admin_fee || 0) + (item.ship_fee || 0);
          return `<div class="item">
            <div class="col-detail">
              <div class="patient-name">${getLastName(item.patient_name)}</div>
              <div class="meta">YOB: ${getBirthYear(item.patient_dob)}${item.accession ? ` &nbsp;|&nbsp; Acc#: <span class="${redactPHI ? 'redacted' : ''}">${redact(item.accession)}</span>` : ''}</div>
              ${item.doctor_name || item.phlebotomist_name ? `<div class="meta">${item.doctor_name ? `Dr: ${item.doctor_name}` : ''}${item.doctor_name && item.phlebotomist_name ? ' &nbsp;|&nbsp; ' : ''}${item.phlebotomist_name ? `Phleb: ${item.phlebotomist_name}` : ''}</div>` : ''}
              ${item.tests && item.tests.length > 0 ? `<div class="tests">${item.tests.map(t => `<span class="test-badge">${t}</span>`).join('')}</div>` : ''}
              ${item.diagnosis ? `<div class="icd${redactPHI ? ' redacted' : ''}">ICD: ${redact(item.diagnosis)}</div>` : ''}
            </div>
            <div class="col-date"><span class="item-date">${formatDate(item.collection_date)}</span></div>
            <div class="col-amount item-amount">
              <span class="item-total">${formatCurrency(itemTotal)}</span>
              <span class="item-breakdown">Draw $${(item.draw_fee||0).toFixed(2)} + Admin $${(item.admin_fee||0).toFixed(2)}${item.ship_fee > 0 ? ` + Ship $${(item.ship_fee||0).toFixed(2)}` : ''}</span>
            </div>
          </div>`;
        }).join('')}
      </div>
      <div class="summary">
        <div>
          <div class="summary-label">Total Due</div>
          <div class="summary-draws">${detail.line_items.length} draws</div>
        </div>
        <div class="summary-total ${isPaid ? 'paid' : ''}">${formatCurrency(inv.total_amount)}</div>
      </div>
      <div class="fees">
        <div><div class="fee-label">Draw Fees</div><div class="fee-value">${formatCurrency(totalDraw)}</div></div>
        <div><div class="fee-label">Admin Fees</div><div class="fee-value">${formatCurrency(totalAdmin)}</div></div>
        <div><div class="fee-label">Shipping</div><div class="fee-value">${formatCurrency(totalShip)}</div></div>
      </div>
      <div class="footer">
        <span style="font-weight:600">AtHomeBloodDraw.com</span>
        <span>Questions? Contact support@athomeblooddraw.com</span>
      </div>
    </div>`;
  };

  const printStyles = `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #333; }
    .invoice-page { page-break-after: always; }
    .invoice-page:last-child { page-break-after: avoid; }
    .header { background: linear-gradient(to right, #276749, #38a169); color: white; padding: 40px; }
    .header-flex { display: flex; justify-content: space-between; align-items: flex-start; }
    .brand { font-size: 16px; font-weight: 800; letter-spacing: 0.5px; }
    .title { font-size: 32px; font-weight: 800; margin-top: 4px; }
    .subtitle { font-size: 12px; opacity: 0.7; margin-top: 4px; }
    .inv-label { font-size: 10px; opacity: 0.6; text-transform: uppercase; letter-spacing: 2px; }
    .inv-number { font-size: 22px; font-weight: 800; letter-spacing: 1px; margin-top: 2px; }
    .inv-date { font-size: 11px; opacity: 0.6; margin-top: 4px; }
    .status-bar { padding: 12px 40px; border-bottom: 1px solid #eee; display: flex; justify-content: space-between; align-items: center; }
    .status-bar.paid { background: #f0fff4; border-color: #c6f6d5; }
    .status-bar.unpaid { background: #fffaf0; border-color: #feebc8; }
    .status-text { font-size: 13px; font-weight: 700; }
    .status-text.paid { color: #276749; }
    .status-text.unpaid { color: #c05621; }
    .status-amount { font-size: 20px; font-weight: 800; }
    .status-amount.paid { color: #276749; }
    .status-amount.unpaid { color: #c05621; }
    .bill-to { padding: 24px 40px; display: flex; justify-content: space-between; }
    .bill-label { font-size: 10px; color: #999; text-transform: uppercase; font-weight: 700; letter-spacing: 1px; margin-bottom: 4px; }
    .bill-name { font-size: 16px; font-weight: 700; }
    .bill-email { font-size: 13px; color: #777; margin-top: 2px; }
    .badge { display: inline-block; padding: 4px 14px; border-radius: 999px; font-size: 12px; font-weight: 700; }
    .badge.paid { background: #c6f6d5; color: #276749; }
    .badge.unpaid { background: #fed7d7; color: #c53030; }
    .divider { border: none; border-top: 1px solid #eee; }
    .items { padding: 20px 40px; }
    .items-header { display: flex; padding-bottom: 12px; margin-bottom: 16px; border-bottom: 2px solid #e2e8f0; }
    .items-header span { font-size: 10px; font-weight: 800; color: #999; text-transform: uppercase; letter-spacing: 1px; }
    .col-detail { flex: 1; }
    .col-date { width: 120px; }
    .col-amount { width: 90px; text-align: right; }
    .item { display: flex; padding: 14px 0; border-bottom: 1px solid #f0f0f0; }
    .item:last-child { border-bottom: none; }
    .patient-name { font-size: 13px; font-weight: 700; }
    .meta { font-size: 11px; color: #777; margin-top: 3px; }
    .tests { margin-top: 6px; }
    .test-badge { display: inline-block; background: #f0fff4; color: #276749; border: 1px solid #c6f6d5; font-size: 10px; font-weight: 600; padding: 2px 8px; border-radius: 999px; margin-right: 4px; margin-bottom: 2px; }
    .icd { font-size: 11px; color: #999; margin-top: 4px; }
    .redacted { filter: blur(5px); user-select: none; -webkit-user-select: none; pointer-events: none; }
    .item-date { font-size: 13px; color: #555; display: flex; align-items: center; }
    .item-amount { text-align: right; display: flex; flex-direction: column; justify-content: center; }
    .item-total { font-size: 13px; font-weight: 700; }
    .item-breakdown { font-size: 9px; color: #999; }
    .summary { background: #f7fafc; padding: 16px 40px; border-top: 2px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
    .summary-label { font-size: 10px; color: #999; text-transform: uppercase; font-weight: 700; }
    .summary-draws { font-size: 13px; color: #777; }
    .summary-total { font-size: 24px; font-weight: 800; }
    .summary-total.paid { color: #276749; }
    .fees { padding: 16px 40px; border-top: 1px solid #eee; display: flex; gap: 40px; }
    .fee-label { font-size: 10px; color: #999; font-weight: 700; }
    .fee-value { font-size: 13px; font-weight: 600; color: #555; }
    .footer { padding: 20px 40px; display: flex; justify-content: space-between; font-size: 11px; color: #999; margin-top: 20px; }
    @media print {
      body { padding: 0; }
      .header, .status-bar, .summary, .test-badge, .badge { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  `;

  // Build full HTML document for print preview
  const buildPrintDoc = (bodyHtml, title) =>
    `<!DOCTYPE html><html><head><title>${title} - AtHomeBloodDraw.com</title><style>${printStyles}</style></head><body>${bodyHtml}</body></html>`;

  // Print single invoice — opens overlay
  const handlePrintInvoice = (inv) => {
    const html = buildInvoiceHtml(inv);
    if (!html) return;
    setPrintHtml(buildPrintDoc(html, inv.invoice_number));
    onPrintOpen();
  };

  // Print all selected invoices — opens overlay
  const handlePrintSelected = () => {
    const selectedInvs = invoices.filter(inv => selectedInvoices.includes(inv.id));
    if (selectedInvs.length === 0) {
      toast({ title: 'Select invoices', description: 'Please select invoices to print.', status: 'warning', duration: 3000 });
      return;
    }
    const allHtml = selectedInvs.map(inv => buildInvoiceHtml(inv)).join('');
    const title = selectedInvs.length === 1 ? selectedInvs[0].invoice_number : `${selectedInvs.length} Invoices`;
    setPrintHtml(buildPrintDoc(allHtml, title));
    onPrintOpen();
  };

  // Trigger browser print from the iframe
  const handlePrintFromOverlay = () => {
    const iframe = document.getElementById('print-preview-iframe');
    if (iframe?.contentWindow) {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }
  };

  return (
    <Box minH="100vh" bg="gray.50" py={{ base: 4, md: 8 }}>
      {/* Demo banner */}
      <Box bg="purple.600" color="white" py={2} textAlign="center" position="sticky" top={0} zIndex={10}>
        <Text fontSize="sm" fontWeight="600">PREVIEW MODE — This is a demo with mock data</Text>
      </Box>

      <Container maxW="900px" px={{ base: 3, md: 6 }} mt={4}>

        {/* Header Card */}
        <Box bg="white" borderRadius="2xl" boxShadow="xl" overflow="hidden" border="1px solid" borderColor="gray.100" mb={6}>
          <Box bgGradient="linear(to-r, #276749, #38a169)" color="white" px={{ base: 6, md: 10 }} py={8} position="relative" overflow="hidden">
            <Box position="absolute" top={0} right={0} bottom={0} left={0} opacity={0.05}
              bgImage="radial-gradient(circle at 25% 25%, white 1px, transparent 1px)" bgSize="20px 20px" />
            <Flex justify="space-between" align="flex-start" position="relative">
              <VStack align="start" spacing={2}>
                <Text fontSize="lg" fontWeight="800" letterSpacing="0.5px">AtHomeBloodDraw.com</Text>
                <Heading size="xl" fontWeight="800" letterSpacing="-0.5px">INVOICES</Heading>
                <Text fontSize="sm" opacity={0.7}>Mobile Phlebotomy Services</Text>
              </VStack>
              <VStack align="end" spacing={1}>
                <Text fontSize="md" fontWeight="700">{MOCK_LAB.name}</Text>
                <Text fontSize="xs" opacity={0.7}>{invoices.length} total invoices</Text>
              </VStack>
            </Flex>
          </Box>

          {/* Summary Bar */}
          <Box px={{ base: 6, md: 10 }} py={4} bg="gray.50" borderBottom="1px solid" borderColor="gray.100">
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
              <Box>
                <Text fontSize="xs" color="gray.400" fontWeight="bold" textTransform="uppercase">Unpaid</Text>
                <Text fontSize="xl" fontWeight="800" color="orange.600">{unpaidInvoices.length}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.400" fontWeight="bold" textTransform="uppercase">Unpaid Total</Text>
                <Text fontSize="xl" fontWeight="800" color="orange.600">
                  {formatCurrency(unpaidInvoices.reduce((s, i) => s + i.total_amount, 0))}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.400" fontWeight="bold" textTransform="uppercase">Paid</Text>
                <Text fontSize="xl" fontWeight="800" color="green.600">{paidInvoices.length}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.400" fontWeight="bold" textTransform="uppercase">Cards on File</Text>
                <Text fontSize="xl" fontWeight="800" color="gray.700">{savedCards.length}</Text>
              </Box>
            </SimpleGrid>
          </Box>
        </Box>

        {/* Card on File & Charge Section */}
        <Box bg="white" borderRadius="2xl" boxShadow="md" border="1px solid" borderColor="gray.100" mb={6} overflow="hidden">
          <Box px={{ base: 4, md: 6 }} py={4} bg="blue.50" borderBottom="1px solid" borderColor="blue.100">
            <HStack spacing={2}>
              <Icon as={FaWallet} color="blue.500" />
              <Text fontSize="md" fontWeight="700" color="blue.700">Card on File</Text>
            </HStack>
          </Box>
          <Box px={{ base: 4, md: 6 }} py={5}>
            {savedCards.length > 0 ? (
              <VStack spacing={4} align="stretch">
                <Select
                  value={selectedCard}
                  onChange={(e) => setSelectedCard(e.target.value)}
                  size="md" borderRadius="lg"
                  icon={<FaRegCreditCard />}
                >
                  {savedCards.map(card => (
                    <option key={card.id} value={card.id}>
                      {card.brand.charAt(0).toUpperCase() + card.brand.slice(1)} •••• {card.last4} — Exp {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
                    </option>
                  ))}
                </Select>
                <HStack spacing={3}>
                  <Button
                    colorScheme="green" size="md" flex={1}
                    leftIcon={<FaCreditCard />}
                    onClick={() => handleChargeSavedCard(selectedCard)}
                    isDisabled={selectedInvoices.length === 0 || !selectedCard}
                  >
                    Charge Selected ({selectedInvoices.length})  —  {formatCurrency(selectedTotal)}
                  </Button>
                  <Button
                    size="md" variant="outline" colorScheme="gray"
                    leftIcon={<FaPrint />}
                    onClick={handlePrintSelected}
                    isDisabled={selectedInvoices.length === 0}
                  >
                    Print ({selectedInvoices.length})
                  </Button>
                  <Button
                    size="md" variant="outline" colorScheme="green"
                    leftIcon={<FaPlus />}
                    onClick={onAddCardOpen}
                  >
                    Add Card
                  </Button>
                </HStack>
                {selectedInvoices.length === 0 && (
                  <Text fontSize="xs" color="gray.400" textAlign="center">Select invoices below to charge this card</Text>
                )}
              </VStack>
            ) : (
              <VStack spacing={3}>
                <Text fontSize="sm" color="gray.500">No cards on file yet.</Text>
                <Button
                  size="md" colorScheme="green" variant="outline"
                  leftIcon={<FaPlus />}
                  onClick={onAddCardOpen}
                >
                  Add Card on File
                </Button>
              </VStack>
            )}
          </Box>
        </Box>

        {/* Unpaid Invoices */}
        {unpaidInvoices.length > 0 && (
          <Box bg="white" borderRadius="2xl" boxShadow="md" border="1px solid" borderColor="gray.100" mb={6} overflow="hidden">
            <Box px={{ base: 4, md: 6 }} py={4} bg="orange.50" borderBottom="1px solid" borderColor="orange.100">
              <HStack spacing={2}>
                <Icon as={FaCreditCard} color="orange.500" />
                <Text fontSize="md" fontWeight="700" color="orange.700">Unpaid Invoices</Text>
              </HStack>
            </Box>
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th w="40px">
                      <Checkbox
                        isChecked={selectedInvoices.length === unpaidInvoices.length && unpaidInvoices.length > 0}
                        isIndeterminate={selectedInvoices.length > 0 && selectedInvoices.length < unpaidInvoices.length}
                        onChange={selectAllUnpaid}
                        colorScheme="green"
                      />
                    </Th>
                    <Th>Invoice</Th>
                    <Th>Batch</Th>
                    <Th>Date</Th>
                    <Th isNumeric>Draws</Th>
                    <Th isNumeric>Amount</Th>
                    <Th w="120px"></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {unpaidInvoices.map(inv => (
                    <Tr key={inv.id} _hover={{ bg: 'gray.50' }} cursor="pointer" onClick={() => toggleInvoice(inv.id)}>
                      <Td onClick={e => e.stopPropagation()}>
                        <Checkbox
                          isChecked={selectedInvoices.includes(inv.id)}
                          onChange={() => toggleInvoice(inv.id)}
                          colorScheme="green"
                        />
                      </Td>
                      <Td><Text fontWeight="700" fontSize="sm">{inv.invoice_number}</Text></Td>
                      <Td><Text fontSize="xs" color="gray.500">{inv.batch_number || '—'}</Text></Td>
                      <Td><Text fontSize="xs" color="gray.600">{formatDate(inv.created_at)}</Text></Td>
                      <Td isNumeric><Text fontSize="xs" color="gray.600">{inv.draw_count}</Text></Td>
                      <Td isNumeric><Text fontWeight="700" fontSize="sm" color="gray.800">{formatCurrency(inv.total_amount)}</Text></Td>
                      <Td onClick={e => e.stopPropagation()}>
                        <HStack spacing={1}>
                          <Button size="xs" variant="ghost" colorScheme="blue" leftIcon={<FaEye />}
                            onClick={() => openInvoiceDetail(inv)}>
                            View
                          </Button>
                          <Button size="xs" variant="ghost" colorScheme="gray" leftIcon={<FaPrint />}
                            onClick={() => handlePrintInvoice(inv)}>
                            PDF
                          </Button>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>
        )}

        {/* Paid Invoices */}
        {paidInvoices.length > 0 && (
          <Box bg="white" borderRadius="2xl" boxShadow="md" border="1px solid" borderColor="gray.100" mb={6} overflow="hidden">
            <Box px={{ base: 4, md: 6 }} py={4} bg="green.50" borderBottom="1px solid" borderColor="green.100">
              <HStack spacing={2}>
                <Icon as={FaCheckCircle} color="green.500" />
                <Text fontSize="md" fontWeight="700" color="green.700">Paid Invoices</Text>
              </HStack>
            </Box>
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Invoice</Th>
                    <Th>Batch</Th>
                    <Th>Date</Th>
                    <Th isNumeric>Draws</Th>
                    <Th isNumeric>Amount</Th>
                    <Th>Status</Th>
                    <Th w="120px"></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {paidInvoices.map(inv => (
                    <Tr key={inv.id} _hover={{ bg: 'gray.50' }}>
                      <Td><Text fontWeight="600" fontSize="sm">{inv.invoice_number}</Text></Td>
                      <Td><Text fontSize="xs" color="gray.500">{inv.batch_number || '—'}</Text></Td>
                      <Td><Text fontSize="xs" color="gray.600">{formatDate(inv.created_at)}</Text></Td>
                      <Td isNumeric><Text fontSize="xs" color="gray.600">{inv.draw_count}</Text></Td>
                      <Td isNumeric><Text fontWeight="600" fontSize="sm" color="gray.700">{formatCurrency(inv.total_amount)}</Text></Td>
                      <Td><Badge colorScheme="green" borderRadius="full" px={2}>PAID</Badge></Td>
                      <Td>
                        <HStack spacing={1}>
                          <Button size="xs" variant="ghost" colorScheme="blue" leftIcon={<FaEye />}
                            onClick={() => openInvoiceDetail(inv)}>
                            View
                          </Button>
                          <Button size="xs" variant="ghost" colorScheme="gray" leftIcon={<FaPrint />}
                            onClick={() => handlePrintInvoice(inv)}>
                            PDF
                          </Button>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>
        )}

        {/* Footer */}
        <Flex justify="space-between" align="center" mt={6} flexWrap="wrap" gap={2}>
          <Text fontSize="xs" color="gray.500" fontWeight="600">AtHomeBloodDraw.com</Text>
          <Text fontSize="xs" color="gray.400">Questions? Contact support@athomeblooddraw.com</Text>
        </Flex>

        {/* ===== INVOICE DETAIL OVERLAY MODAL ===== */}
        <Modal isOpen={isViewOpen} onClose={onViewClose} size="xl" scrollBehavior="inside">
          <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
          <ModalContent borderRadius="2xl" mx={4} overflow="hidden" maxH="90vh">
            {viewingInvoice && viewingDetail && (
              <>
                {/* Header */}
                <Box bgGradient="linear(to-r, #276749, #38a169)" color="white" px={{ base: 6, md: 10 }} py={6} position="relative" overflow="hidden" flexShrink={0}>
                  <Box position="absolute" top={0} right={0} bottom={0} left={0} opacity={0.05}
                    bgImage="radial-gradient(circle at 25% 25%, white 1px, transparent 1px)" bgSize="20px 20px" />
                  <Flex justify="space-between" align="flex-start" position="relative">
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" fontWeight="800" letterSpacing="0.5px">AtHomeBloodDraw.com</Text>
                      <Heading size="lg" fontWeight="800" letterSpacing="-0.5px">INVOICE</Heading>
                      <Text fontSize="xs" opacity={0.7}>Mobile Phlebotomy Services</Text>
                    </VStack>
                    <VStack align="end" spacing={1}>
                      <Text fontSize="xs" opacity={0.6} textTransform="uppercase" letterSpacing="wider">Invoice No.</Text>
                      <Text fontSize="xl" fontWeight="800" letterSpacing="1px">{viewingInvoice.invoice_number}</Text>
                      <Text fontSize="xs" opacity={0.6}>Issued {formatDate(viewingInvoice.created_at)}</Text>
                    </VStack>
                  </Flex>
                  {/* Close button */}
                  <Box as="button" position="absolute" top={3} right={3} onClick={onViewClose}
                    color="whiteAlpha.800" _hover={{ color: 'white' }} p={2}>
                    <Icon as={FaTimes} boxSize={4} />
                  </Box>
                </Box>

                {/* Status Bar */}
                <Box
                  bg={isViewingPaid ? 'green.50' : 'orange.50'}
                  px={{ base: 6, md: 10 }} py={3}
                  borderBottom="1px solid" borderColor={isViewingPaid ? 'green.100' : 'orange.100'}
                  flexShrink={0}
                >
                  <Flex justify="space-between" align="center">
                    <HStack spacing={2}>
                      <Icon as={isViewingPaid ? FaCheckCircle : FaCreditCard} color={isViewingPaid ? 'green.500' : 'orange.500'} boxSize={4} />
                      <Text fontSize="sm" fontWeight="bold" color={isViewingPaid ? 'green.700' : 'orange.700'}>
                        {isViewingPaid ? 'Payment Received' : 'Payment Required'}
                      </Text>
                    </HStack>
                    <Text fontSize="xl" fontWeight="800" color={isViewingPaid ? 'green.700' : 'orange.700'}>
                      {formatCurrency(viewingInvoice.total_amount)}
                    </Text>
                  </Flex>
                </Box>

                <ModalBody px={0} py={0}>
                  {/* Bill To Section */}
                  <Box px={{ base: 6, md: 10 }} py={5}>
                    <SimpleGrid columns={2} spacing={4}>
                      <Box>
                        <Text fontSize="xs" color="gray.400" textTransform="uppercase" fontWeight="bold" letterSpacing="wider" mb={1}>Bill To</Text>
                        <Text fontSize="md" fontWeight="700" color="gray.800">{MOCK_LAB.name}</Text>
                        <Text fontSize="sm" color="gray.500" mt={0.5}>{MOCK_LAB.email}</Text>
                      </Box>
                      <Box textAlign="right">
                        <Text fontSize="xs" color="gray.400" textTransform="uppercase" fontWeight="bold" letterSpacing="wider" mb={1}>Status</Text>
                        <Badge
                          colorScheme={isViewingPaid ? 'green' : 'red'}
                          fontSize="sm" px={4} py={1.5} borderRadius="full" fontWeight="700"
                        >
                          {isViewingPaid ? 'PAID' : 'UNPAID'}
                        </Badge>
                      </Box>
                    </SimpleGrid>
                  </Box>

                  <Divider borderColor="gray.100" />

                  {/* Line Items */}
                  <Box px={{ base: 6, md: 10 }} py={5}>
                    <Grid
                      templateColumns={{ base: '1fr auto', md: '1fr 120px 90px' }}
                      gap={4} pb={3} mb={4}
                      borderBottom="2px solid" borderColor="gray.200"
                    >
                      <Text fontSize="xs" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="wider">Service Details</Text>
                      <Text fontSize="xs" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="wider" display={{ base: 'none', md: 'block' }}>Date</Text>
                      <Text fontSize="xs" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="wider" textAlign="right">Amount</Text>
                    </Grid>

                    <VStack spacing={0} align="stretch" divider={<Divider borderColor="gray.100" />}>
                      {viewingDetail.line_items.map((item, i) => {
                        const itemTotal = (item.draw_fee || 0) + (item.admin_fee || 0) + (item.ship_fee || 0);
                        return (
                          <Grid
                            key={i}
                            templateColumns={{ base: '1fr auto', md: '1fr 120px 90px' }}
                            gap={4} py={3}
                            _hover={{ bg: 'gray.50' }}
                            mx={-3} px={3} borderRadius="lg"
                            transition="background 0.15s"
                          >
                            <GridItem>
                              <HStack spacing={3} align="flex-start">
                                <Box bg="green.50" borderRadius="lg" p={2} mt={0.5} flexShrink={0}>
                                  <Icon as={FaVial} color="green.400" boxSize={3} />
                                </Box>
                                <Box flex={1}>
                                  <Text fontSize="sm" fontWeight="700" color="gray.800">{getLastName(item.patient_name)}</Text>
                                  <HStack spacing={3} mt={1} flexWrap="wrap">
                                    {item.patient_dob && (
                                      <HStack spacing={1}>
                                        <Text fontSize="xs" color="gray.400">YOB:</Text>
                                        <Text fontSize="xs" color="gray.600" fontWeight="500">{getBirthYear(item.patient_dob)}</Text>
                                      </HStack>
                                    )}
                                    {item.accession && (
                                      <HStack spacing={1}>
                                        <Text fontSize="xs" color="gray.400">Acc#:</Text>
                                        <Text fontSize="xs" color="gray.600" fontWeight="600" fontFamily="mono" filter={redactPHI ? 'blur(5px)' : 'none'} userSelect={redactPHI ? 'none' : 'auto'}>{item.accession}</Text>
                                      </HStack>
                                    )}
                                  </HStack>
                                  {(item.doctor_name || item.phlebotomist_name) && (
                                    <HStack spacing={4} mt={1} flexWrap="wrap">
                                      {item.doctor_name && (
                                        <HStack spacing={1}>
                                          <Icon as={FaStethoscope} color="gray.300" boxSize={2.5} />
                                          <Text fontSize="xs" color="gray.500">{item.doctor_name}</Text>
                                        </HStack>
                                      )}
                                      {item.phlebotomist_name && (
                                        <HStack spacing={1}>
                                          <Icon as={FaSyringe} color="gray.300" boxSize={2.5} />
                                          <Text fontSize="xs" color="gray.500">{item.phlebotomist_name}</Text>
                                        </HStack>
                                      )}
                                    </HStack>
                                  )}
                                  {item.tests && item.tests.length > 0 && (
                                    <HStack spacing={1} mt={1.5} flexWrap="wrap">
                                      {item.tests.map((test, ti) => (
                                        <Badge
                                          key={ti} bg="green.50" color="green.600"
                                          fontSize="10px" fontWeight="600" px={2} py={0.5}
                                          borderRadius="full" border="1px solid" borderColor="green.100"
                                        >
                                          {test}
                                        </Badge>
                                      ))}
                                    </HStack>
                                  )}
                                  {item.diagnosis && (
                                    <HStack spacing={1} mt={1}>
                                      <Text fontSize="xs" color="gray.400" fontWeight="600">ICD:</Text>
                                      <Text fontSize="xs" color="gray.500" filter={redactPHI ? 'blur(5px)' : 'none'} userSelect={redactPHI ? 'none' : 'auto'}>{item.diagnosis}</Text>
                                    </HStack>
                                  )}
                                </Box>
                              </HStack>
                            </GridItem>
                            <GridItem display={{ base: 'none', md: 'flex' }} alignItems="center">
                              <Text fontSize="sm" color="gray.600">{formatDate(item.collection_date)}</Text>
                            </GridItem>
                            <GridItem display="flex" alignItems="center" justifyContent="flex-end">
                              <VStack spacing={0} align="end">
                                <Text fontSize="sm" fontWeight="700" color="gray.800">{formatCurrency(itemTotal)}</Text>
                                <Text fontSize="10px" color="gray.400">
                                  Draw {formatCurrency(item.draw_fee)} + Admin {formatCurrency(item.admin_fee)}
                                  {item.ship_fee > 0 ? ` + Ship ${formatCurrency(item.ship_fee)}` : ''}
                                </Text>
                              </VStack>
                            </GridItem>
                          </Grid>
                        );
                      })}
                    </VStack>
                  </Box>

                  {/* Summary Section */}
                  <Box bg="gray.50" px={{ base: 6, md: 10 }} py={4} borderTop="2px solid" borderColor="gray.200">
                    <Flex justify="space-between" align="center">
                      <VStack align="start" spacing={0}>
                        <Text fontSize="xs" color="gray.400" textTransform="uppercase" fontWeight="bold">Total Due</Text>
                        <Text fontSize="sm" color="gray.500">{viewingDetail.line_items.length} draws</Text>
                      </VStack>
                      <Text fontSize="2xl" fontWeight="800" color={isViewingPaid ? 'green.600' : 'gray.800'}>
                        {formatCurrency(viewingInvoice.total_amount)}
                      </Text>
                    </Flex>
                  </Box>

                  {/* Fee Breakdown */}
                  <Box px={{ base: 6, md: 10 }} py={4} borderTop="1px solid" borderColor="gray.100">
                    <SimpleGrid columns={3} spacing={4}>
                      <Box>
                        <Text fontSize="xs" color="gray.400" fontWeight="bold">Draw Fees</Text>
                        <Text fontSize="sm" fontWeight="600" color="gray.700">
                          {formatCurrency(viewingDetail.line_items.reduce((s, i) => s + (i.draw_fee || 0), 0))}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.400" fontWeight="bold">Admin Fees</Text>
                        <Text fontSize="sm" fontWeight="600" color="gray.700">
                          {formatCurrency(viewingDetail.line_items.reduce((s, i) => s + (i.admin_fee || 0), 0))}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.400" fontWeight="bold">Shipping</Text>
                        <Text fontSize="sm" fontWeight="600" color="gray.700">
                          {formatCurrency(viewingDetail.line_items.reduce((s, i) => s + (i.ship_fee || 0), 0))}
                        </Text>
                      </Box>
                    </SimpleGrid>
                  </Box>

                  {/* Pay Button */}
                  {!isViewingPaid && (
                    <Box px={{ base: 6, md: 10 }} py={5} borderTop="1px solid" borderColor="gray.100">
                      <Button
                        bgGradient="linear(to-r, #276749, #38a169)"
                        color="white" size="lg" width="full"
                        leftIcon={<Icon as={FaCreditCard} />}
                        fontSize="md" py={7} borderRadius="xl"
                        _hover={{ bgGradient: 'linear(to-r, #38a169, #48bb78)', transform: 'translateY(-1px)', boxShadow: 'lg' }}
                        _active={{ transform: 'translateY(0)' }}
                        transition="all 0.2s" boxShadow="md"
                        onClick={() => { onViewClose(); setTimeout(onInvoicePayOpen, 200); }}
                      >
                        Pay {formatCurrency(viewingInvoice.total_amount)}
                      </Button>
                      <HStack spacing={1.5} justify="center" mt={3}>
                        <Icon as={FaLock} color="gray.400" boxSize={3} />
                        <Text fontSize="xs" color="gray.400">Secure payment powered by Stripe</Text>
                      </HStack>
                    </Box>
                  )}

                  {/* Payment Success in overlay */}
                  {isViewingPaid && viewingInvoice.payment_status !== 'paid' && paymentSuccess[viewingInvoice.id] && (
                    <Box px={{ base: 6, md: 10 }} py={4}>
                      <Box bg="green.50" border="1px solid" borderColor="green.200" borderRadius="lg" p={4}>
                        <HStack spacing={2}>
                          <Icon as={FaCheckCircle} color="green.500" />
                          <Box>
                            <Text fontWeight="bold" color="green.700">Payment received!</Text>
                            <Text fontSize="sm" color="green.600">Thank you for your payment.</Text>
                          </Box>
                        </HStack>
                      </Box>
                    </Box>
                  )}
                </ModalBody>
              </>
            )}
          </ModalContent>
        </Modal>

        {/* ===== INVOICE PAYMENT MODAL ===== */}
        <Modal isOpen={isInvoicePayOpen} onClose={onInvoicePayClose} isCentered size="md">
          <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
          <ModalContent borderRadius="2xl" mx={4} overflow="hidden">
            <Box bgGradient="linear(to-r, #276749, #38a169)" px={6} py={4}>
              <Flex justify="space-between" align="center">
                <VStack align="start" spacing={0}>
                  <Text color="white" fontSize="lg" fontWeight="700">Payment</Text>
                  <Text color="whiteAlpha.800" fontSize="sm">{formatCurrency(viewingInvoice?.total_amount)} — {viewingInvoice?.invoice_number}</Text>
                </VStack>
                <Box as="button" onClick={onInvoicePayClose} color="whiteAlpha.800" _hover={{ color: 'white' }} p={1}>
                  <Icon as={FaTimes} boxSize={4} />
                </Box>
              </Flex>
            </Box>
            <ModalBody px={6} py={5}>
              <VStack spacing={4} align="stretch">
                {/* Tabs: Saved Card / New Card */}
                <HStack spacing={0} bg="gray.100" borderRadius="lg" p={1}>
                  <Button
                    flex={1} size="sm" borderRadius="md"
                    bg={paymentTab === 'saved' ? 'white' : 'transparent'}
                    color={paymentTab === 'saved' ? 'green.600' : 'gray.500'}
                    boxShadow={paymentTab === 'saved' ? 'sm' : 'none'}
                    fontWeight="600"
                    onClick={() => setPaymentTab('saved')}
                    leftIcon={<FaRegCreditCard />}
                    _hover={{ bg: paymentTab === 'saved' ? 'white' : 'gray.50' }}
                  >
                    Card on File
                  </Button>
                  <Button
                    flex={1} size="sm" borderRadius="md"
                    bg={paymentTab === 'new' ? 'white' : 'transparent'}
                    color={paymentTab === 'new' ? 'green.600' : 'gray.500'}
                    boxShadow={paymentTab === 'new' ? 'sm' : 'none'}
                    fontWeight="600"
                    onClick={() => setPaymentTab('new')}
                    leftIcon={<FaCreditCard />}
                    _hover={{ bg: paymentTab === 'new' ? 'white' : 'gray.50' }}
                  >
                    New Card
                  </Button>
                </HStack>

                {paymentTab === 'saved' ? (
                  <VStack spacing={4} align="stretch">
                    {savedCards.length > 0 ? (
                      <>
                        <Select
                          value={selectedCard}
                          onChange={(e) => setSelectedCard(e.target.value)}
                          size="md" borderRadius="lg"
                          icon={<FaRegCreditCard />}
                        >
                          {savedCards.map(card => (
                            <option key={card.id} value={card.id}>
                              {card.brand.charAt(0).toUpperCase() + card.brand.slice(1)} •••• {card.last4} — Exp {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
                            </option>
                          ))}
                        </Select>
                        <Button
                          bgGradient="linear(to-r, #276749, #38a169)" color="white" size="lg" width="full"
                          leftIcon={<Icon as={FaCreditCard} />}
                          fontSize="md" py={7} borderRadius="xl"
                          _hover={{ bgGradient: 'linear(to-r, #38a169, #48bb78)', transform: 'translateY(-1px)', boxShadow: 'lg' }}
                          _active={{ transform: 'translateY(0)' }}
                          transition="all 0.2s" boxShadow="md"
                          onClick={handleInvoicePay}
                          isDisabled={!selectedCard}
                        >
                          Charge {formatCurrency(viewingInvoice?.total_amount)}
                        </Button>
                      </>
                    ) : (
                      <Box bg="gray.50" borderRadius="lg" p={4} textAlign="center">
                        <Text fontSize="sm" color="gray.500" mb={3}>No cards on file yet.</Text>
                      </Box>
                    )}
                    <Divider />
                    <Button
                      size="sm" variant="outline" colorScheme="green"
                      leftIcon={<FaPlus />}
                      onClick={() => { onInvoicePayClose(); setTimeout(onAddCardOpen, 200); }}
                    >
                      Add New Card on File
                    </Button>
                  </VStack>
                ) : (
                  <VStack spacing={4} align="stretch">
                    <Box>
                      <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Card Number</Text>
                      <Box as="input" w="full" p={3} border="1px solid" borderColor="gray.200" borderRadius="lg" fontSize="sm"
                        placeholder="4242 4242 4242 4242" _focus={{ borderColor: 'green.400', outline: 'none', boxShadow: '0 0 0 1px #38a169' }} />
                    </Box>
                    <HStack spacing={3}>
                      <Box flex={1}>
                        <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Expiry</Text>
                        <Box as="input" w="full" p={3} border="1px solid" borderColor="gray.200" borderRadius="lg" fontSize="sm"
                          placeholder="MM / YY" _focus={{ borderColor: 'green.400', outline: 'none', boxShadow: '0 0 0 1px #38a169' }} />
                      </Box>
                      <Box flex={1}>
                        <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>CVC</Text>
                        <Box as="input" w="full" p={3} border="1px solid" borderColor="gray.200" borderRadius="lg" fontSize="sm"
                          placeholder="123" _focus={{ borderColor: 'green.400', outline: 'none', boxShadow: '0 0 0 1px #38a169' }} />
                      </Box>
                    </HStack>
                    <Button
                      bgGradient="linear(to-r, #276749, #38a169)" color="white" size="lg" width="full"
                      leftIcon={<Icon as={FaCreditCard} />}
                      fontSize="md" py={7} borderRadius="xl"
                      _hover={{ bgGradient: 'linear(to-r, #38a169, #48bb78)', transform: 'translateY(-1px)', boxShadow: 'lg' }}
                      _active={{ transform: 'translateY(0)' }}
                      transition="all 0.2s" boxShadow="md"
                      onClick={handleInvoicePay}
                    >
                      Pay {formatCurrency(viewingInvoice?.total_amount)}
                    </Button>
                  </VStack>
                )}

                <HStack spacing={1.5} justify="center" pb={1}>
                  <Icon as={FaLock} color="gray.400" boxSize={3} />
                  <Text fontSize="xs" color="gray.400">Secure payment powered by Stripe</Text>
                </HStack>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Add Card Modal (Mock) */}
        <Modal isOpen={isAddCardOpen} onClose={onAddCardClose} isCentered size="md">
          <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
          <ModalContent borderRadius="2xl" mx={4} overflow="hidden">
            <Box bgGradient="linear(to-r, #276749, #38a169)" px={6} py={4}>
              <Flex justify="space-between" align="center">
                <VStack align="start" spacing={0}>
                  <Text color="white" fontSize="lg" fontWeight="700">Add Card on File</Text>
                  <Text color="whiteAlpha.800" fontSize="sm">Save for future payments</Text>
                </VStack>
                <Box as="button" onClick={onAddCardClose} color="whiteAlpha.800" _hover={{ color: 'white' }} p={1}>
                  <Icon as={FaTimes} boxSize={4} />
                </Box>
              </Flex>
            </Box>
            <ModalBody px={6} py={5}>
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Card Number</Text>
                  <Box as="input" w="full" p={3} border="1px solid" borderColor="gray.200" borderRadius="lg" fontSize="sm"
                    placeholder="4242 4242 4242 4242" _focus={{ borderColor: 'green.400', outline: 'none', boxShadow: '0 0 0 1px #38a169' }} />
                </Box>
                <HStack spacing={3}>
                  <Box flex={1}>
                    <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Expiry</Text>
                    <Box as="input" w="full" p={3} border="1px solid" borderColor="gray.200" borderRadius="lg" fontSize="sm"
                      placeholder="MM / YY" _focus={{ borderColor: 'green.400', outline: 'none', boxShadow: '0 0 0 1px #38a169' }} />
                  </Box>
                  <Box flex={1}>
                    <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>CVC</Text>
                    <Box as="input" w="full" p={3} border="1px solid" borderColor="gray.200" borderRadius="lg" fontSize="sm"
                      placeholder="123" _focus={{ borderColor: 'green.400', outline: 'none', boxShadow: '0 0 0 1px #38a169' }} />
                  </Box>
                </HStack>
                <Button
                  bgGradient="linear(to-r, #276749, #38a169)" color="white" size="lg" width="full"
                  leftIcon={<Icon as={FaPlus} />}
                  fontSize="md" py={7} borderRadius="xl"
                  _hover={{ bgGradient: 'linear(to-r, #38a169, #48bb78)', transform: 'translateY(-1px)', boxShadow: 'lg' }}
                  _active={{ transform: 'translateY(0)' }}
                  transition="all 0.2s" boxShadow="md"
                  onClick={handleMockAddCard}
                >
                  Save Card
                </Button>
                <HStack spacing={1.5} justify="center" pb={1}>
                  <Icon as={FaLock} color="gray.400" boxSize={3} />
                  <Text fontSize="xs" color="gray.400">Your card is securely saved via Stripe</Text>
                </HStack>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Charge Confirmation Modal */}
        <Modal isOpen={isConfirmOpen} onClose={() => { onConfirmClose(); setPendingChargeCardId(null); }} isCentered size="sm">
          <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
          <ModalContent borderRadius="2xl" mx={4} overflow="hidden">
            <Box bgGradient="linear(to-r, #276749, #38a169)" px={6} py={4}>
              <Flex justify="space-between" align="center">
                <Text color="white" fontSize="lg" fontWeight="700">Confirm Payment</Text>
                <Box as="button" onClick={() => { onConfirmClose(); setPendingChargeCardId(null); }} color="whiteAlpha.800" _hover={{ color: 'white' }} p={1}>
                  <Icon as={FaTimes} boxSize={4} />
                </Box>
              </Flex>
            </Box>
            <ModalBody px={6} py={6}>
              <VStack spacing={4} align="stretch">
                <Text fontSize="md" color="gray.700" textAlign="center">
                  You are about to charge
                </Text>
                <Text fontSize="3xl" fontWeight="800" color="gray.800" textAlign="center">
                  {formatCurrency(selectedTotal)}
                </Text>
                {pendingChargeCardId && (
                  <Box bg="gray.50" borderRadius="lg" p={3} textAlign="center">
                    <Text fontSize="sm" color="gray.600">
                      {(() => {
                        const card = savedCards.find(c => c.id === pendingChargeCardId);
                        return card ? `${card.brand.charAt(0).toUpperCase() + card.brand.slice(1)} •••• ${card.last4}` : '';
                      })()}
                    </Text>
                  </Box>
                )}
                <Text fontSize="sm" color="gray.500" textAlign="center">
                  for {selectedInvoices.length} invoice{selectedInvoices.length !== 1 ? 's' : ''}
                </Text>
                <Divider />
                <HStack spacing={3}>
                  <Button
                    flex={1} variant="outline" size="lg"
                    onClick={() => { onConfirmClose(); setPendingChargeCardId(null); }}
                  >
                    Cancel
                  </Button>
                  <Button
                    flex={1} size="lg"
                    bgGradient="linear(to-r, #276749, #38a169)" color="white"
                    leftIcon={<Icon as={FaCreditCard} />}
                    _hover={{ bgGradient: 'linear(to-r, #38a169, #48bb78)', transform: 'translateY(-1px)', boxShadow: 'lg' }}
                    _active={{ transform: 'translateY(0)' }}
                    transition="all 0.2s" boxShadow="md"
                    onClick={confirmCharge}
                  >
                    Confirm
                  </Button>
                </HStack>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Print Preview Overlay */}
        <Modal isOpen={isPrintOpen} onClose={onPrintClose} size="full" closeOnOverlayClick>
          <ModalOverlay bg="blackAlpha.800" />
          <ModalContent bg="transparent" boxShadow="none" my={0} maxW="100vw">
            <ModalBody p={0} display="flex" flexDirection="column" alignItems="center" justifyContent="center" minH="100vh">
              <Flex w="100%" maxW="850px" justify="flex-end" mb={2} gap={3} px={4} pt={4}>
                <Button colorScheme="green" leftIcon={<FaPrint />} onClick={handlePrintFromOverlay} size="sm" fontWeight="600">
                  Print
                </Button>
                <Button variant="ghost" color="white" onClick={onPrintClose} size="sm" _hover={{ bg: 'whiteAlpha.200' }}>
                  <Icon as={FaTimes} />
                </Button>
              </Flex>
              <Box w="100%" maxW="850px" h="calc(100vh - 80px)" bg="white" borderRadius="md" overflow="hidden" mx="auto">
                <iframe
                  id="print-preview-iframe"
                  title="Print Preview"
                  srcDoc={printHtml || ''}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                />
              </Box>
            </ModalBody>
          </ModalContent>
        </Modal>

      </Container>
    </Box>
  );
}
